import React, { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import {
  useLinkToken,
  useExchangeToken,
  useSyncTransactions,
} from "../../hooks/usePlaid";
import { Building2, Loader2 } from "lucide-react";
import { useToast } from "../ToastProvider/ToastProvider";
import { translations } from "../../../translations/translations";

interface PlaidLinkButtonProps {
  token?: string | null;
  onSuccess?: () => void;
  onExit?: () => void;
  tooltipText?: string;
  language?: "en" | "es";
  t?: any;
  disabled?: boolean;
  userTier?: "free" | "standard" | "premium";
  hideTooltip?: boolean; // ✅ NEW: Allow parent to disable tooltip
}

/**
 * Plaid Link IconButton Component
 * Opens Plaid Link flow to connect bank accounts
 * Uses Material-UI IconButton style
 */
const PlaidLinkButton: React.FC<PlaidLinkButtonProps> = ({
  token,
  onSuccess,
  onExit,
  tooltipText,
  language = "en",
  t: propT,
  disabled = false,
  userTier = "premium",
  hideTooltip = false, // ✅ NEW: Default to showing tooltip
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const toast = useToast();

  // Use prop t if provided, otherwise get from translations
  const t = propT || translations[language];

  // Helper function to get user-friendly error message
  const getPlaidErrorMessage = (error: any): string => {
    const errorCode = error?.response?.data?.errorCode;
    const errorMessage = error?.response?.data?.message;
    const requiresReauth = error?.response?.data?.requiresReauth;

    // If backend already provided a user message, use it
    if (errorMessage && typeof errorMessage === 'string') {
      return errorMessage;
    }

    // Map error codes to translation keys
    switch (errorCode) {
      case 'ITEM_LOGIN_REQUIRED':
        return t.plaidLink.errors.itemLoginRequired;
      case 'ITEM_LOCKED':
        return t.plaidLink.errors.itemLocked;
      case 'ITEM_NOT_SUPPORTED':
        return t.plaidLink.errors.itemNotSupported;
      case 'RATE_LIMIT_EXCEEDED':
        return t.plaidLink.errors.rateLimitExceeded;
      default:
        if (requiresReauth) {
          return t.plaidLink.errors.itemLoginRequired;
        }
        return t.plaidLink.errors.genericError;
    }
  };

  // Fetch link token
  const {
    data: linkTokenData,
    refetch: fetchLinkToken,
    isLoading: isLoadingToken,
    error: tokenError,
  } = useLinkToken(token);

  // Exchange public token
  const { mutate: exchangeToken, isPending: isExchanging } = useExchangeToken(token);

  // Sync transactions after linking
  const { mutate: syncTransactions } = useSyncTransactions(token);

  // ✅ Auto-fetch token on mount (only once)
  useEffect(() => {
    if (!linkToken && !isLoadingToken && userTier === "premium") {
      console.log("🔗 Auto-fetching link token on mount...");
      fetchLinkToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Run only once on mount

  // ✅ Update link token when fetched
  useEffect(() => {
    if (linkTokenData?.link_token && !linkToken) {
      console.log(
        "✅ Link token received:",
        linkTokenData.link_token.substring(0, 20) + "..."
      );
      setLinkToken(linkTokenData.link_token);
    }
  }, [linkTokenData, linkToken]);

  // Handle successful link
  const handleOnSuccess = useCallback(
    (public_token: string) => {
      exchangeToken(public_token, {
        onSuccess: () => {
          console.log("✅ Bank account linked successfully");

          syncTransactions(undefined, {
            onSuccess: (data) => {
              console.log("✅ Transactions synced:", data);

              const successMessage = t.plaidLink.bankLinkedSuccess
                .replace('{count}', data.transactionCount)
                .replace('{incomes}', data.incomesCreated)
                .replace('{expenses}', data.expensesCreated);

              toast.success(successMessage);

              onSuccess?.();
            },
            onError: (error) => {
              console.error("⚠️ Failed to sync transactions:", error);
              const errorMessage = getPlaidErrorMessage(error);
              toast.error(errorMessage);
            },
          });
        },
        onError: (error) => {
          console.error("❌ Error exchanging token:", error);
          const errorMessage = getPlaidErrorMessage(error);
          toast.error(errorMessage);
        },
      });
    },
    [exchangeToken, syncTransactions, onSuccess, language, toast, t]
  );

  // Handle exit
  const handleOnExit = useCallback(() => {
    console.log("User exited Plaid Link");
    onExit?.();
  }, [onExit]);

  // Initialize Plaid Link
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  });

  // ✅ Auto-open when ready (for triggering from navbar)
  useEffect(() => {
    if (hideTooltip && linkToken && ready) {
      console.log("🚀 Auto-opening Plaid Link...");
      open();
    }
  }, [hideTooltip, linkToken, ready, open]);

  // ✅ Handle button click with better logic
  const handleClick = () => {
    if (linkToken && ready) {
      console.log("🚀 Opening Plaid Link...");
      open();
    } else if (!isLoadingToken && !linkToken) {
      console.log("🔄 Retrying token fetch...");
      fetchLinkToken();
    }
  };

  // ✅ Check userTier AFTER all hooks
  if (userTier !== "premium") {
    return null;
  }

  // ✅ Show error state if token fetch failed
  if (tokenError) {
    const errorButton = (
      <button
        onClick={() => fetchLinkToken()}
        className="icon-btn error"
        aria-label="Retry bank connection"
        style={{
          background: "var(--error-bg)",
          border: "1px solid var(--error-text)",
          borderRadius: 8,
          padding: "0.5rem",
          cursor: "pointer",
          color: "var(--error-text)",
        }}
      >
        <Building2 size={24} />
      </button>
    );

    return hideTooltip ? (
      errorButton
    ) : (
      <span title={t.plaidLink.failedToConnect}>{errorButton}</span>
    );
  }

  // ✅ Default tooltip text
  const defaultTooltip = t.plaidLink.linkBankAccount;

  // ✅ Determine icon/spinner
  const buttonIcon =
    isLoadingToken || isExchanging ? (
      <Loader2 size={24} className="animate-spin" />
    ) : (
      <Building2 size={24} />
    );

  // ✅ The button itself
  const button = (
    <button
      onClick={handleClick}
      className="icon-btn"
      disabled={disabled || isLoadingToken || isExchanging}
      aria-label={
        language === "en" ? t.plaidLink.linkBankAccount : t.plaidLink.linkBankAccount
      }
      title={tooltipText || defaultTooltip}
      style={{
        background: "transparent",
        border: "none",
        borderRadius: 8,
        padding: "0.5rem",
        cursor: disabled || isLoadingToken || isExchanging ? "not-allowed" : "pointer",
        color: "var(--text-color)",
        opacity: disabled || isLoadingToken || isExchanging ? 0.5 : 1,
      }}
    >
      {buttonIcon}
    </button>
  );

  // ✅ Return with or without tooltip based on hideTooltip prop
  return hideTooltip ? (
    button
  ) : (
    button
  );
};

export default PlaidLinkButton;